import { useCallback } from 'react';

import { useTracking } from '../../../context/TrackingContext';
import { EventType } from '../../../types/events';

/**
 * Hook for tracking JotCoach-specific analytics events
 * Wraps the generic useTracking hook with JotCoach-specific event methods
 */
export function useJotCoachTracking() {
  const { trackEvent } = useTracking();

  // Overview events
  const trackOverviewRendered = useCallback(
    (data: { jotcoach_session_id: string; note_id: string; quality_score?: number | null }) => {
      trackEvent(EventType.JOTCOACH_OVERVIEW_RENDERED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'overview' as const,
        item_id: 'overview',
        quality_score: data.quality_score,
      });
    },
    [trackEvent],
  );

  const trackOverviewViewed = useCallback(
    (data: { jotcoach_session_id: string; note_id: string; quality_score?: number | null }) => {
      trackEvent(EventType.JOTCOACH_OVERVIEW_VIEWED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'overview' as const,
        item_id: 'overview',
        quality_score: data.quality_score,
      });
    },
    [trackEvent],
  );

  const trackOverviewExpanded = useCallback(
    (data: { jotcoach_session_id: string; note_id: string; quality_score?: number | null }) => {
      trackEvent(EventType.JOTCOACH_OVERVIEW_EXPANDED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'overview' as const,
        item_id: 'overview',
        quality_score: data.quality_score,
      });
    },
    [trackEvent],
  );

  const trackOverviewCollapsed = useCallback(
    (data: { jotcoach_session_id: string; note_id: string; quality_score?: number | null }) => {
      trackEvent(EventType.JOTCOACH_OVERVIEW_COLLAPSED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'overview' as const,
        item_id: 'overview',
        quality_score: data.quality_score,
      });
    },
    [trackEvent],
  );

  // Gap events
  const trackGapRendered = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      gap_id: string;
      section_id: string;
      priority?: 'High' | 'Medium' | 'Low' | null;
      gap_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_GAP_RENDERED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'gap' as const,
        item_id: data.gap_id,
        section_id: data.section_id,
        priority: data.priority,
        gap_title: data.gap_title,
      });
    },
    [trackEvent],
  );

  const trackGapViewed = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      gap_id: string;
      section_id: string;
      priority?: 'High' | 'Medium' | 'Low' | null;
      gap_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_GAP_VIEWED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'gap' as const,
        item_id: data.gap_id,
        section_id: data.section_id,
        priority: data.priority,
        gap_title: data.gap_title,
      });
    },
    [trackEvent],
  );

  const trackGapExpanded = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      gap_id: string;
      section_id: string;
      priority?: 'High' | 'Medium' | 'Low' | null;
      gap_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_GAP_EXPANDED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'gap' as const,
        item_id: data.gap_id,
        section_id: data.section_id,
        priority: data.priority,
        gap_title: data.gap_title,
      });
    },
    [trackEvent],
  );

  const trackGapCollapsed = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      gap_id: string;
      section_id: string;
      priority?: 'High' | 'Medium' | 'Low' | null;
      gap_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_GAP_COLLAPSED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'gap' as const,
        item_id: data.gap_id,
        section_id: data.section_id,
        priority: data.priority,
        gap_title: data.gap_title,
      });
    },
    [trackEvent],
  );

  const trackGapCopied = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      gap_id: string;
      section_id: string;
      priority?: 'High' | 'Medium' | 'Low' | null;
      gap_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_GAP_COPIED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'gap' as const,
        item_id: data.gap_id,
        section_id: data.section_id,
        priority: data.priority,
        gap_title: data.gap_title,
      });
    },
    [trackEvent],
  );

  // Question events
  const trackQuestionRendered = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      question_id: string;
      section_id: string;
      question_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_QUESTION_RENDERED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'question' as const,
        item_id: data.question_id,
        section_id: data.section_id,
        question_title: data.question_title,
      });
    },
    [trackEvent],
  );

  const trackQuestionViewed = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      question_id: string;
      section_id: string;
      question_title?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_QUESTION_VIEWED, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: 'question' as const,
        item_id: data.question_id,
        section_id: data.section_id,
        question_title: data.question_title,
      });
    },
    [trackEvent],
  );

  // Feedback events
  const trackFeedbackUseful = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      item_type: 'overview' | 'gap' | 'question';
      item_id: string;
      section_id?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_FEEDBACK_USEFUL, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: data.item_type,
        item_id: data.item_id,
        section_id: data.section_id,
      });
    },
    [trackEvent],
  );

  const trackFeedbackNotUseful = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      item_type: 'overview' | 'gap' | 'question';
      item_id: string;
      section_id?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_FEEDBACK_NOT_USEFUL, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: data.item_type,
        item_id: data.item_id,
        section_id: data.section_id,
      });
    },
    [trackEvent],
  );

  const trackFeedbackComment = useCallback(
    (data: {
      jotcoach_session_id: string;
      note_id: string;
      item_type: 'overview' | 'gap' | 'question';
      item_id: string;
      section_id?: string | null;
    }) => {
      trackEvent(EventType.JOTCOACH_FEEDBACK_COMMENT, {
        jotcoach_session_id: data.jotcoach_session_id,
        note_id: data.note_id,
        item_type: data.item_type,
        item_id: data.item_id,
        section_id: data.section_id,
      });
    },
    [trackEvent],
  );

  return {
    // Overview tracking
    trackOverviewRendered,
    trackOverviewViewed,
    trackOverviewExpanded,
    trackOverviewCollapsed,

    // Gap tracking
    trackGapRendered,
    trackGapViewed,
    trackGapExpanded,
    trackGapCollapsed,
    trackGapCopied,

    // Question tracking
    trackQuestionRendered,
    trackQuestionViewed,

    // Feedback tracking
    trackFeedbackUseful,
    trackFeedbackNotUseful,
    trackFeedbackComment,
  };
}
